import type { EditorReactComponent } from '@wix/ambassador-devcenter-v1-component-type-data/types';
import {
  resolveRuntimeOverrides,
  type ResolveWarning,
} from '@wix/component-protocol/enricher';
import type { EditorElement } from '@wix/component-protocol/schema';
import { CompRef } from '@wix/document-services-types';

import { deepMerge, setByPath } from '../utils';
import {
  EditorReactComponentExtensionType,
  IManifestStoreEntity,
} from './types';
import { IManifestMiddleware, ManifestStore } from './ManifestStore';
import { VirtualManifestElementType } from '../builders';
import {
  createEditorPlatformManifestServicesError,
  EditorPlatformManifestServicesErrorCode,
} from '../errors';
import type { VirtualElementProvider } from '../api/providers/VirtualElementProvider';
import { ManifestServiceFlags } from '../ManifestServiceFlags';
import type {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

export class ManifestOverridesResolver {
  #middlewares: Set<IManifestMiddleware> = new Set();

  constructor(
    private readonly editorPointerAPI: EditorPointerServiceAPI,
    private readonly manifestStore: ManifestStore,
    private readonly virtualElementProvider: VirtualElementProvider,
    private readonly manifestServiceFlags: ManifestServiceFlags,
  ) {}

  withMiddleware(middleware: IManifestMiddleware) {
    this.#middlewares.add(middleware);
    return this;
  }

  resolve(
    editorPointer: EditorPointer<CompRef>,
    item: IManifestStoreEntity<EditorReactComponentExtensionType>,
  ): EditorReactComponent {
    if (this.editorPointerAPI.getInnerElementPath(editorPointer)?.length) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.ManifestOverrideResolverError,
        'resolve should run only on root node',
      );
    }

    const rootOverrides = this.#applyMiddlewares(editorPointer, item);

    const editorElement = deepMerge(
      item.manifest.editorElement!,
      rootOverrides.editorElement!,
    );

    return {
      ...item.manifest,
      editorElement,
    };
  }

  #applyMiddlewares(
    editorPointer: EditorPointer<CompRef>,
    store: IManifestStoreEntity<EditorReactComponentExtensionType>,
  ) {
    if (this.editorPointerAPI.getInnerElementPath(editorPointer)?.length) {
      throw createEditorPlatformManifestServicesError(
        EditorPlatformManifestServicesErrorCode.ManifestOverrideResolverError,
        '#applyMiddlewares should run only on root node',
      );
    }

    const node = this.virtualElementProvider.buildVirtualElement({
      cacheNamespace: `ManifestOverridesResolver––${editorPointer.asUniqueKey}`,
      contextBuilder: (builder) => {
        builder.withManifest(editorPointer, {
          editorElement: store.manifest.editorElement!,
          type: store.componentType,
        });
      },
    });

    const getRawManifest = (_editorPointer: EditorPointer<CompRef>) =>
      this.manifestStore.getResolvedEditorElement(_editorPointer, {
        useRawManifest: true,
      })!;

    const baseEditorElement = store.manifest.editorElement!;

    return [...this.#middlewares].reduce<EditorReactComponent>(
      (manifest, middleware) => {
        try {
          const refChildrenOverrides = node
            .getChildren({ recursive: true })
            .filter(
              (child) =>
                child.getVirtualManifest().tree.elementType ===
                VirtualManifestElementType.RefElement,
            )
            .reverse()
            .map((child) => {
              const childOverrides = middleware.resolveChildRefElement(
                child,
                getRawManifest,
              );

              const breadcrumbs = child.getVirtualManifest().tree.breadcrumbs;

              if (!childOverrides) {
                return {
                  breadcrumbs,
                  overrides: null,
                };
              }

              const resolvedChildOverrides =
                this.#shouldResolveRuntimeOverrides(middleware)
                  ? this.#resolveRuntimeOverrides(
                      child.getManifest()?.editorElement ?? {},
                      childOverrides as Partial<EditorElement>,
                      middleware,
                      editorPointer,
                    )
                  : childOverrides;

              return {
                breadcrumbs,
                overrides: {
                  editorElement: setByPath(
                    {},
                    breadcrumbs,
                    resolvedChildOverrides,
                  ),
                },
              };
            })
            .filter((x) => x.overrides);

          const rootOverrides = middleware.resolveRootElement(
            node,
            getRawManifest,
          );

          if (!rootOverrides && !refChildrenOverrides.length) {
            return manifest;
          }

          let result = manifest;
          const applyOverrides = (overrides: EditorReactComponent | null) => {
            if (!overrides) {
              return;
            }

            if (!this.#shouldResolveRuntimeOverrides(middleware)) {
              result = deepMerge(result, overrides);
              return;
            }

            // Overrides are applied in chunks (A, then B), and each runtime resolution is independent.
            // If base is always the original manifest: A => base+A, B => base+B (A is lost).
            // Using runtimeBase = base + accumulated gives: A => base+A, B => (base+A)+B.
            const runtimeBase = deepMerge(
              baseEditorElement,
              result.editorElement ?? {},
            );
            const resolvedEditorElement = this.#resolveRuntimeOverrides(
              runtimeBase,
              overrides.editorElement ?? {},
              middleware,
              editorPointer,
            );

            result = {
              ...result,
              editorElement: resolvedEditorElement,
            };
          };

          refChildrenOverrides.forEach(({ overrides }) => {
            if (!overrides) {
              return;
            }

            result = deepMerge(result, overrides);
          });

          applyOverrides(rootOverrides);

          return result;
        } catch (e) {
          console.error(
            createEditorPlatformManifestServicesError(
              EditorPlatformManifestServicesErrorCode.ManifestOverrideResolverError,
            ).withParentError(e as any),
          );

          return manifest;
        }
      },
      {},
    );
  }

  #shouldResolveRuntimeOverrides(middleware: IManifestMiddleware) {
    return (
      this.manifestServiceFlags.isRuntimeManifestOverridesResolutionEnabled &&
      !!middleware.isExternalMiddleware
    );
  }

  #logRuntimeOverrideWarnings(
    middleware: IManifestMiddleware,
    editorPointer: EditorPointer<CompRef>,
    warnings: ResolveWarning[],
  ) {
    if (!warnings.length) {
      return;
    }

    const warningSummary = warnings.map((warning) => ({
      path: warning.path,
      reason: warning.reason,
      message: warning.message,
    }));

    const error = createEditorPlatformManifestServicesError(
      EditorPlatformManifestServicesErrorCode.ManifestOverrideResolverWarning,
      'runtime manifest overrides were partially applied',
    )
      .withMessage(`middleware=${middleware.constructor.name}`)
      .withMessage(`editorPointer=${editorPointer.asUniqueKey}`)
      .withWarnings(warningSummary);

    console.warn(error.message);
  }

  #resolveRuntimeOverrides(
    base: EditorElement,
    overrides: Partial<EditorElement>,
    middleware: IManifestMiddleware,
    editorPointer: EditorPointer<CompRef>,
  ) {
    const { result, warnings } = resolveRuntimeOverrides({
      base,
      overrides,
    });

    this.#logRuntimeOverrideWarnings(middleware, editorPointer, warnings);

    return result;
  }
}
